import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from 'axios';
import config from "../../config";

const AUTH_KEY = "auth";

const initialState = {
  isLoggedIn: sessionStorage.getItem(AUTH_KEY) === "true",
  user: null,
  userID: null,
  email: null,
  msalEmail: null,
  googleEmail: null,
  linkedInEmail: null,
  accessToken: null,
  googleAccessToken: sessionStorage.getItem("google_access_token") || null,
  linkedin_access_token: sessionStorage.getItem("linkedin_access_token") || null,
  token: null,
  refreshToken: sessionStorage.getItem("refresh_token") || null,
  loading: false,
  error: null,
  tokenExpiry: null,
  maxUploadFileSize: null,
  subscription: null,
  planUploadLimit: null
};

// Store token API call
export const storeTokenAuth = createAsyncThunk('auth/storeTokenAuth', async ({ idToken }, { rejectWithValue }) => {
  try {
    // console.log('🔄 Calling store-token API with token:', idToken);

    const response = await axios.post(
      `${config.backend_Url}/auth/store-token`,
      { token: idToken },
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );

    const data = response.data;
    // console.log('📦 Raw response from store-token:', data);

    if (!data || data.status === false) {
      throw new Error(data?.message || 'Token verification failed');
    }

    return {
      userExists: data.userExists === true, // STRICTLY use backend value
      email: data.email,
      token: data.token,
      user: data.user || null,
      organizationId: data.organizationId || null
    };
  } catch (error) {
    console.error('Store token error:', error);
    return rejectWithValue({
      message: error.response?.data?.message || error.message || 'Token verification failed'
    });
  }
});

// login thunk
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const payload = {
        UserToken: "",
        Data: {
          email: credentials.email,
          password: credentials.password
        },
        Filter: []
      };
      const response = await axios.post(`${config.backend_Url}users/signin`, payload, {
        headers: { 
          'accept': 'application/json',
          'Content-Type': 'application/json' 
        }
      });

      const data = response.data;
      // console.log('Raw response from login:', data);

      // Backend may return { success, message, data } or flat fields
      const isSuccess = data?.success === true || data?.status === true;
      if (!data || !isSuccess) {
        throw new Error(data?.message || 'Incorrect email or password');
      }

      const payloadSource = data.data || data;
      // console.log('📦 Raw response from login:', payloadSource);
      return {
        userID: payloadSource.id || null,
        user: payloadSource.name || payloadSource.user || null,
        email: payloadSource.email || null,
        accessToken: payloadSource.access_token || payloadSource.token || null,
        refreshToken: payloadSource.refresh_token || null,
        organizationId: payloadSource.id || payloadSource.organizationId || null,
        tokenExpiry: payloadSource.expires_in ? Date.now() + (payloadSource.expires_in * 1000) : null,
        maxUploadFileSize: payloadSource.max_upload_file_size_limit_mb || null,
        subscription: payloadSource?.subscription || null,
        planUploadLimit: payloadSource?.plan_upload_limit || null
      };
    } catch (error) {
      console.error('Login error:', error);
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Login failed'
      });
    }
  }
);

export const signup = createAsyncThunk(
  'auth/signup',
  async (userData, { rejectWithValue }) => {
    try {
      const payload = {
        UserToken: "",
        Data: {
          first_name: userData.firstName,
          second_name: userData.lastName,
          email: userData.email,
          country: userData.country,
          state: userData.state,
          city: userData.city,
          contact: userData.phone,
          password: userData.password,
          confirm_password: userData.confirm,
          domain: userData.domain,
          organization: userData.organization,
        },
        Filter: []
      };

      const response = await axios.post(`${config.backend_Url}users/signup-step1`, payload, {
        headers: { 
          'accept': 'application/json',
          'Content-Type': 'application/json' 
        }
      });

      const data = response.data;
      // console.log('📦 Raw response from signup:', data);

      if (!data || data.success === false) {
        throw new Error(data?.message || 'Signup failed');
      }

      return {
        message: data.message,
        status: data.success,
      };
    } catch (error) {
      console.error('Signup error:', error);
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Signup failed'
      });
    }
  }
);

// Google Login thunk
export const loginWithGoogle = createAsyncThunk(
  'auth/loginWithGoogle',
  async ({ credential }, { rejectWithValue }) => {
    try {
      
      const baseUrl = config.backend_Url.endsWith('/') ? config.backend_Url : `${config.backend_Url}/`;
      
      const response = await axios.post(`${baseUrl}auth/google-auth`, { credential }, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      const data = response.data;

      if (!data || data.success === false) {
        throw new Error(data?.message || 'Google authentication failed');
      }

      const payloadSource = data.data || data;
      return {
        userID: payloadSource.id || null,
        user: payloadSource.name || payloadSource.user || null,
        googleEmail: payloadSource.email || null,
        googleAccessToken: payloadSource.access_token || null,
        organizationId: payloadSource.id || payloadSource.organizationId || null,
        maxUploadFileSize: payloadSource.max_upload_file_size_limit_mb || null,
        subscription: payloadSource?.subscription || null,
        planUploadLimit: payloadSource?.plan_upload_limit || null,
        message: data.message || 'Google authentication successful',
      };
    } catch (error) {
      console.error('Google Auth error:', error);
      return rejectWithValue({
        message: error.response?.data?.message || error.response?.data?.error || error.message || 'Google Auth failed'
      });
    }
  }
);

// LinkedIn Login thunk
export const loginWithLinkedIn = createAsyncThunk(
  'auth/loginWithLinkedIn',
  async ({ code }, { rejectWithValue }) => {
    try {
      const baseUrl = config.backend_Url.endsWith('/') ? config.backend_Url : `${config.backend_Url}/`;
      
      const response = await axios.post(`${baseUrl}auth/linkedin-auth`, { code }, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      const data = response.data;

      if (!data || data.success === false) {
        throw new Error(data?.message || 'LinkedIn authentication failed');
      }

      const payloadSource = data.data || data;
      return {
        userID: payloadSource.id || null,
        user: payloadSource.name || payloadSource.user || null,
        linkedInEmail: payloadSource.email || null,
        linkedin_access_token: payloadSource.linkedin_access_token || null,
        organizationId: payloadSource.id || payloadSource.organizationId || null,
        maxUploadFileSize: payloadSource.max_upload_file_size_limit_mb || null,
        subscription: payloadSource?.subscription || null,
        planUploadLimit: payloadSource?.plan_upload_limit || null,
        message: data.message || 'LinkedIn authentication successful',
      };
    } catch (error) {
      console.error('LinkedIn Auth error:', error);
      return rejectWithValue({
        message: error.response?.data?.message || error.response?.data?.error || error.message || 'LinkedIn Auth failed'
      });
    }
  }
);

// Verify Email OTP
export const verifyEmailOtp = createAsyncThunk(
  'auth/verifyEmailOtp',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const payload = {
        UserToken: "",
        Data: { email, otp },
        Filter: []
      };
      const response = await axios.post(`${config.backend_Url}users/verify-otp`, payload, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      const data = response.data;
      // console.log('📦 Raw response from verifyEmailOtp:', data);
      const isSuccess = data?.success === true || data?.status === true;
      if (!data || !isSuccess) {
        throw new Error(data?.message || 'Invalid OTP');
      }
      const src = data.data || data;
      return {
        message: data.message || 'Email verified',
        status: data.success,
        token: src.token || null,
        user: src.user || src.name || null
      };
    } catch (error) {
      console.error('verifyEmailOtp error:', error);
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Verification failed'
      });
    }
  }
);

// Resend Email OTP
export const resendEmailOtp = createAsyncThunk(
  'auth/resendEmailOtp',
  async ({ email }, { rejectWithValue }) => {
    try {
      const payload = {
        email
      };
      const response = await axios.post(`${config.backend_Url}users/resend-otp`, payload, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      const data = response.data;
      // console.log('📦 Raw response from resendEmailOtp:', data);
      const isSuccess = data?.success === true || data?.status === true;
      if (!data || !isSuccess) {
        throw new Error(data?.message || 'Failed to resend OTP');
      }
      return { message: data.message || 'OTP resent' };
    } catch (error) {
      console.error('resendEmailOtp error:', error);
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Resend failed'
      });
    }
  }
);

// Refresh token thunk
export const refreshAccessToken = createAsyncThunk(
  'auth/refreshAccessToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const refreshToken = state.auth.refreshToken;
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(`${config.backend_Url}auth/refresh`, {
        refresh_token: refreshToken
      }, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      const data = response.data;
      // console.log('Token refresh response:', data);

      if (!data || !data.success) {
        throw new Error(data?.message || 'Token refresh failed');
      }

      return {
        accessToken: data.access_token || data.token,
        refreshToken: data.refresh_token || refreshToken, // Use new refresh token if provided
        tokenExpiry: data.expires_in ? Date.now() + (data.expires_in * 1000) : null
      };
    } catch (error) {
      console.error('Token refresh error:', error);
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Token refresh failed'
      });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      // console.log("Login success:", action.payload);
      sessionStorage.setItem(AUTH_KEY, "true");
      state.isLoggedIn = true;
      state.user = action.payload || null;
    },
    logoutSuccess: (state) => {
      sessionStorage.removeItem(AUTH_KEY);
      sessionStorage.removeItem("refresh_token");
      // sessionStorage.removeItem("access_token");
      state.isLoggedIn = false;
      state.user = null;
      state.userID = null;
      state.email = null;
      state.msalEmail = null;
      state.googleEmail = null;
      state.linkedInEmail = null;
      state.accessToken = null;
      state.googleAccessToken = null;
      state.linkedin_access_token = null;
      state.refreshToken = null;
      state.token = null;
      state.tokenExpiry = null;
      state.maxUploadFileSize = null;
      state.subscription = null;
      state.planUploadLimit = null;
    },
    updateTokens: (state, action) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.googleAccessToken = action.payload.googleAccessToken;
      state.linkedin_access_token = action.payload.linkedin_access_token;
      state.tokenExpiry = action.payload.tokenExpiry;
    },
    setMsalEmail: (state, action) => {
      state.msalEmail = action.payload;
    },
    updateSubscription: (state, action) => {
      state.subscription = action.payload;
    },
    updateMaxUploadFileSize: (state, action) => {
      state.maxUploadFileSize = action.payload;
    },
    updatePlanUploadLimit: (state, action) => {
      state.planUploadLimit = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(storeTokenAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(storeTokenAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoggedIn = true;
        state.token = action.payload.token;
      })
      .addCase(storeTokenAuth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoggedIn = true;
        state.user = action.payload.user;
        state.userID = action.payload.userID;
        state.email = action.payload.email;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.token = action.payload.token;
        state.tokenExpiry = action.payload.tokenExpiry;
        state.maxUploadFileSize = action.payload.maxUploadFileSize;
        state.subscription = action.payload.subscription;
        state.planUploadLimit = action.payload.planUploadLimit;
        sessionStorage.setItem(AUTH_KEY, "true");
        // if (action.payload.accessToken) {
        //   sessionStorage.setItem("access_token", action.payload.accessToken);
        // }
        if (action.payload.refreshToken) {
          sessionStorage.setItem("refresh_token", action.payload.refreshToken);
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loading = false;
        // Do not log in on signup-step1; wait until verification completes
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      .addCase(verifyEmailOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmailOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoggedIn = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        sessionStorage.setItem(AUTH_KEY, "true");
      })
      .addCase(verifyEmailOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      .addCase(resendEmailOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendEmailOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resendEmailOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      .addCase(refreshAccessToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.loading = false;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.googleAccessToken = action.payload.googleAccessToken;
        state.linkedin_access_token = action.payload.linkedin_access_token;
        state.tokenExpiry = action.payload.tokenExpiry;
        // if (action.payload.accessToken) {
        //   sessionStorage.setItem("access_token", action.payload.accessToken);
        // }
        if (action.payload.refreshToken) {
          sessionStorage.setItem("refresh_token", action.payload.refreshToken);
        }

        if (action.payload.googleAccessToken) {
          sessionStorage.setItem("google_access_token", action.payload.googleAccessToken);
        }
        if (action.payload.linkedin_access_token) {
          sessionStorage.setItem("linkedin_access_token", action.payload.linkedin_access_token);
        }
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
        // If refresh fails, logout the user
        state.isLoggedIn = false;
        state.user = null;
        state.userID = null;
        state.email = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.googleAccessToken = null;
        state.linkedin_access_token = null;
        state.token = null;
        state.tokenExpiry = null;
        state.maxUploadFileSize = null;
        state.subscription = null;
        state.planUploadLimit = null;
        sessionStorage.removeItem(AUTH_KEY);
        sessionStorage.removeItem("refresh_token");
        sessionStorage.removeItem("google_access_token");
        sessionStorage.removeItem("linkedin_access_token");
      })
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoggedIn = true;
        state.googleEmail = action.payload.googleEmail;
        state.googleAccessToken = action.payload.googleAccessToken;
        state.user = action.payload.user;
        state.userID = action.payload.userID;
        state.maxUploadFileSize = action.payload.maxUploadFileSize;
        state.subscription = action.payload.subscription;
        state.planUploadLimit = action.payload.planUploadLimit;
        sessionStorage.setItem(AUTH_KEY, "true");
        if (action.payload.googleAccessToken) {
          sessionStorage.setItem("google_access_token", action.payload.googleAccessToken);
        }
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      .addCase(loginWithLinkedIn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithLinkedIn.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoggedIn = true;
        state.linkedInEmail = action.payload.linkedInEmail;
        state.linkedin_access_token = action.payload.linkedin_access_token;
        state.user = action.payload.user;
        state.userID = action.payload.userID;
        state.maxUploadFileSize = action.payload.maxUploadFileSize;
        state.subscription = action.payload.subscription;
        state.planUploadLimit = action.payload.planUploadLimit;
        sessionStorage.setItem(AUTH_KEY, "true");
        if (action.payload.linkedin_access_token) {
          sessionStorage.setItem("linkedin_access_token", action.payload.linkedin_access_token);
        }
      })
      .addCase(loginWithLinkedIn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      });
  },
});

export const { loginSuccess, logoutSuccess, setMsalEmail, updateTokens, updateSubscription, updateMaxUploadFileSize,updatePlanUploadLimit } = authSlice.actions;
export default authSlice.reducer;
